// Module-level cache for energy efficiency images to prevent multiple API calls
let cachedEnergyEfficiencyImages = null
let energyEfficiencyImagesPromise = null

export default function useContentfulMedia(appContext) {
  const { $contentfulClient, $hubble } = appContext || {}

  // helpers (reduce complexity)
  const looksEnergy = (s) => {
    const v = String(s || '').toLowerCase()
    return v.includes('energy') || v.includes('efficiency') || v.includes('energi')
  }
  const extractClass = (s) => {
    const str = String(s || '').toUpperCase()
    const m = str.match(/\b([A-G](?:[+-])?)\b/)
    return m ? m[1] : null
  }
  const normalizeAssets = (items) => {
    return items
      // eslint-disable-next-line complexity
      .filter(a => {
        const ct = a?.fields?.file?.contentType || ''
        const title = a?.fields?.title || ''
        const fileName = a?.fields?.file?.fileName || ''
        return ct.startsWith('image/') && (looksEnergy(title) || looksEnergy(fileName))
      })
      .map(a => {
        const title = a?.fields?.title || ''
        const file = a?.fields?.file || {}
        const url = file?.url
        const fileName = file?.fileName
        return {
          id: a?.sys?.id,
          classValue: extractClass(title) || extractClass(fileName),
          image: { imageUrl: url, title, altText: a?.fields?.description || title }
        }
      })
      .filter(x => !!x.image.imageUrl)
  }

  // eslint-disable-next-line complexity
  const fetchEnergyEfficiencyImagesFromAPI = async () => {
    try {
      // Prefer Hubble API if available
      if ($hubble && $hubble.pageResources && $hubble.pageResources.getEnergyEfficiencyAssets) {
        const res = await $hubble.pageResources.getEnergyEfficiencyAssets()
        const list = Array.isArray(res?.data) ? res.data : []
        const result = list.map(it => ({
          id: it.id,
          classValue: it.classValue,
          image: { imageUrl: it.imageUrl, title: it.title, altText: it.altText }
        }))
        cachedEnergyEfficiencyImages = result
        return result
      }
    } catch (e) {
      console.warn('Falling back to Contentful assets for energy images:', e)
    }
    if (!$contentfulClient || !$contentfulClient.getAssets) {
      const result = []
      cachedEnergyEfficiencyImages = result
      return result
    }
    try {
      // Fetch all image assets from Contentful media library (SVG/PNG/JPG)
      const response = await $contentfulClient.getAssets({
        mimetype_group: 'image',
        limit: 1000
      })
      const items = Array.isArray(response?.items) ? response.items : []
      const result = normalizeAssets(items)
      cachedEnergyEfficiencyImages = result
      return result
    } catch (e) {
      console.error('Error fetching energy efficiency images:', e)
      const result = []
      cachedEnergyEfficiencyImages = result
      return result
    }
  }

  const getEnergyEfficiencyImages = () => {
    // Return cached result if available
    if (cachedEnergyEfficiencyImages !== null) {
      return Promise.resolve(cachedEnergyEfficiencyImages)
    }

    // Return existing promise if a request is already in flight
    if (energyEfficiencyImagesPromise) {
      return energyEfficiencyImagesPromise
    }

    // Create new promise for fetching energy efficiency images
    energyEfficiencyImagesPromise = fetchEnergyEfficiencyImagesFromAPI()

    // Clear promise after completion (success or failure)
    energyEfficiencyImagesPromise.then(() => {
      energyEfficiencyImagesPromise = null
    }).catch(() => {
      energyEfficiencyImagesPromise = null
    })

    return energyEfficiencyImagesPromise
  }

  // eslint-disable-next-line complexity
  const getImageByClass = async (classValue) => {
    // Prefer Hubble API lookup first
    try {
      if ($hubble && $hubble.pageResources && $hubble.pageResources.getEnergyEfficiencyAssets) {
        const list = await getEnergyEfficiencyImages()
        const desired = String(classValue || '').toUpperCase()
        const found = list.find(x => (x.classValue || '').toUpperCase() === desired)
        if (found) {
          return { imageUrl: found.image.imageUrl, title: found.image.title, altText: found.image.altText }
        }
      }
    } catch (e) {
      console.warn('Energy class lookup via Hubble failed, trying Contentful assets:', e)
    }
    if (!$contentfulClient || !classValue) {
      return null
    }
    try {
      // Load image assets and find one matching the requested class
      const response = await $contentfulClient.getAssets({ mimetype_group: 'image', limit: 1000 })
      const items = normalizeAssets(Array.isArray(response?.items) ? response.items : [])
      const desired = String(classValue).trim().toUpperCase()
      const match = items.find(a => (a.classValue || '').toUpperCase() === desired)
      if (!match) { return null }
      return { imageUrl: match.image.imageUrl, title: match.image.title, altText: match.image.altText }
    } catch (e) {
      console.error('Error fetching energy efficiency image for class:', classValue, e)
    }
    return null
  }

  return {
    getImageByClass
  }
}
